import { isArray, mergeWith } from "lodash";
import { RootState } from "src/store";
import {
    getProfile as apiGetProfile,
    updateProfile as apiUpdateProfile,
    updateTwoFactor as apiUpdateTwoFactor,
} from "src/ui/containers/account/api";
import { PartialDeep } from "type-fest";

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

type AccountState = {
    profile: API.Profile | null;
};

export const fetchProfile = createAsyncThunk("profile/fetch", apiGetProfile);
export const updateProfile = createAsyncThunk("profile/update", apiUpdateProfile);
export const updateTwoFactor = createAsyncThunk("profile/update/2fa", apiUpdateTwoFactor);

const initialState: AccountState = {
    profile: null,
};

const initialWithLastPersistedState: AccountState = mergeWith(
    initialState,
    {
        // override anything from "initial-state" & "last-persisted-state" here
    },
    (obj: PartialDeep<AccountState>, src: PartialDeep<AccountState>) => {
        if (isArray(obj)) {
            return src;
        }
    },
);

const slice = createSlice({
    name: "account",
    initialState: initialWithLastPersistedState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchProfile.fulfilled, (state: AccountState, { payload }) => {
            if (payload) {
                state.profile = payload;
            }
        });
        // `updateProfile` reports a rejected save by resolving with `success: false` rather than throwing, so
        // this case also runs on failure. Without the check the unsaved form values overwrote the stored profile
        // and the header showed a value the backend had refused.
        builder.addCase(updateProfile.fulfilled, (state: AccountState, { payload, meta }) => {
            if (payload?.success) {
                state.profile = meta.arg;
            }
        });
        builder.addCase(updateTwoFactor.fulfilled, (state, { meta }) => {
            if (state.profile) {
                state.profile.twoFactorEnabled = meta.arg.enabled;
            }
        });
    },
});

export const selectProfile = (state: RootState): API.Profile | null => state.account.profile;

export default slice.reducer;
